import { ClerkProvider, useAuth } from "@clerk/react";
import { light } from "@luckymenu/design-tokens";
import { I18nProvider } from "@luckymenu/i18n/react";
import { createStaffCore, StaffCoreProvider, type StaffPlatform } from "@luckymenu/staff-core";
import { type ReactNode, useRef, useState } from "react";
import { env } from "./env";
import { readStoredSupport } from "./features/session/support";
import { i18n } from "./i18n";
import { webAlerts } from "./platform/alerts";
import { webFiles } from "./platform/files";
import { webNetwork } from "./platform/network";
import { webDeviceName, webDitherImage, webPrintTransports, webRasterise } from "./platform/print";
import { webPush } from "./platform/push";
import { webRealtime } from "./platform/realtime";
import { indexedDbStorage } from "./platform/storage";

declare const __APP_VERSION__: string;
declare const __LM_CDN_URL__: string;

/** Builds the web platform adapters for staff-core, printing included (P5-T11, P5-T12). */
function StaffCore({ children }: { children: ReactNode }) {
	const { getToken, signOut } = useAuth();
	const auth = useRef({ getToken, signOut });
	auth.current = { getToken, signOut };
	const [platform] = useState<StaffPlatform>(() => ({
		platform: "web",
		appVersion: __APP_VERSION__,
		apiUrl: "/api/trpc",
		storage: indexedDbStorage(),
		printTransports: webPrintTransports(),
		rasterise: webRasterise,
		ditherImage: webDitherImage,
		deviceName: webDeviceName(),
		alerts: webAlerts(),
		realtime: webRealtime(),
		network: webNetwork(),
		push: webPush(),
		files: webFiles,
		cdnUrl: __LM_CDN_URL__,
		auth: {
			getToken: () => auth.current.getToken(),
			signOut: () => auth.current.signOut(),
		},
	}));
	// A support session opened in this tab survives a reload (sessionStorage).
	const [core] = useState(() => createStaffCore(platform, { support: readStoredSupport() }));
	return (
		<StaffCoreProvider platform={platform} core={core}>
			{children}
		</StaffCoreProvider>
	);
}

export function Providers({ children }: { children: ReactNode }) {
	return (
		<I18nProvider i18n={i18n}>
			<ClerkProvider
				publishableKey={env.CLERK_PUBLISHABLE_KEY}
				afterSignOutUrl="/"
				appearance={{ variables: { colorPrimary: light.primary, borderRadius: "10px" } }}
			>
				<StaffCore>{children}</StaffCore>
			</ClerkProvider>
		</I18nProvider>
	);
}
